import { TableV2Column } from 'components/Table/TableV2';
import {
  Conversation,
  SearchableConversationSortableFields
} from 'generated-graphql/types';
import { ControlChip, ConversationStatusIconAndLabel } from '../components';
import {
  screeningStatusFilterOptions,
  statusFilterOptions
} from '../constants';
import { ConversationFilterIds } from '../constants/enums';
import { IconButton, Typography } from '@mui/material';
import { toPercentage } from 'utils/calculations';
import { ApplicationStageMapper } from './applicationStageMapper';
import { ApplicationStage } from '../types';
import { stageOptions } from '../constants/stageOptions';
import { controlFilterOptions } from '../constants/controlFilterOptions';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { formatDateFromNow } from 'utils/datetime';
import { ScreeningStatusChip } from '../components/ScreeningStatusChip';
import { CampaignType } from 'services/campaign/types';

export const getSelectedConversationColumns = ({
  onRemoveConversation,
  campaignType
}: {
  onRemoveConversation: (id: string) => void;
  campaignType?: CampaignType | null;
}) => {
  const columns: TableV2Column<Conversation>[] = [
    {
      cell: (row) => (
        <ConversationStatusIconAndLabel
          status={row.status}
          needsReview={!!row.needsReview}
          unresolvedNeedsReviewReasons={row.unresolvedNeedsReviewReasons}
          errorCode={row.errorCode}
          campaignType={campaignType}
          meetingBookingStatus={row.meetingBookingStatus}
        />
      ),
      filterable: true,
      filterId: ConversationFilterIds.status,
      filterOptions: statusFilterOptions,
      center: true,
      width: '52px'
    },
    {
      name: 'Candidate',
      cell: (row) => (
        <Typography variant="body1" color="primary.main">
          {row.participantFirstName} {row.participantLastName || ''}
        </Typography>
      ),
      sortable: true,
      minWidth: '150px',
      sortFunction: (a, b) => {
        const aName = `${a.participantFirstName} ${
          a.participantLastName || ''
        }`;
        const bName = `${b.participantFirstName} ${
          b.participantLastName || ''
        }`;

        return aName.localeCompare(bName);
      }
    },
    {
      name: 'Screening',
      cell: (row) => (
        <ScreeningStatusChip
          status={row.status}
          campaignType={campaignType}
          meetingBookingStatus={row.meetingBookingStatus}
        />
      ),
      filterable: true,
      filterId: ConversationFilterIds.screeningStatus,
      filterOptions: screeningStatusFilterOptions
    },
    {
      name: 'Score',
      sortable: true,
      cell: (row) =>
        row.preferredAnswersScore !== null &&
        row.preferredAnswersScore !== undefined
          ? toPercentage(row.preferredAnswersScore)
          : '-',
      sortFunction: (a, b) => {
        return (a.preferredAnswersScore || 0) - (b.preferredAnswersScore || 0);
      }
    },
    {
      name: 'Stage',
      selector: (row) => {
        if (!row.applicationStage) {
          return 'No stage';
        }

        return ApplicationStageMapper[row.applicationStage as ApplicationStage];
      },
      filterable: true,
      filterId: ConversationFilterIds.applicationStage,
      filterOptions: stageOptions
    },
    {
      name: 'Last activity',
      selector: (row) => formatDateFromNow(row.updatedAt),
      sortable: true,
      id: SearchableConversationSortableFields.UpdatedAt,
      sortField: SearchableConversationSortableFields.UpdatedAt
    },
    {
      name: 'Control',
      cell: (row) => <ControlChip isHumanTakeover={row.isHumanTakeover} />,
      filterable: true,
      filterId: ConversationFilterIds.control,
      filterOptions: controlFilterOptions,
      width: '120px'
    },
    {
      cell: (row) => (
        <IconButton
          aria-label="remove conversation"
          onClick={() => onRemoveConversation(row.id)}
          color="error"
        >
          <DeleteOutlineIcon />
        </IconButton>
      ),
      isAction: true,
      width: '52px'
    }
  ];

  return columns;
};
